import { fgColumnEntries } from "@redotech/redo-fulfillment-common/views/fulfillment/fg-column-catalog";
import {
  FulfillmentGroupData,
  getScanItemQuantity,
  hasBomExpansion,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoTablePaddedCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";
import { getExpandedItems } from "../../utils/outbound-labels-utils";

const catalogEntry = fgColumnEntries["scan-verified"];

function getInitials(
  firstName: string | undefined | null,
  lastName: string | undefined | null,
  name: string | undefined | null,
) {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  return (
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() ?? ""
  );
}

function getUserInitials(userId: string, users: OMSUser[]): string {
  const user = users.find((u) => u._id === userId);
  if (!user) {
    return "";
  }
  return getInitials(user.firstName, user.lastName, user.name);
}

function getItemScanInfo(
  row: FulfillmentGroupData,
  expansionIndex: number,
): { scanned: boolean; verifiedBy: string | null } | null {
  if (!row.scanVerifiedItemized) {
    return null;
  }

  const expandedItems = getExpandedItems(row);
  const item = expandedItems[expansionIndex];
  if (!item) {
    return null;
  }

  const externalId = item.externalId;

  const userScans = row.scanVerifiedItemized[externalId];
  if (!userScans || userScans.length === 0) {
    return { scanned: false, verifiedBy: null };
  }

  const totalVerified = userScans.reduce(
    (sum, scan) => sum + scan.quantityVerified,
    0,
  );

  let sameItemCount = 0;
  for (let i = 0; i < expansionIndex; i++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (expandedItems[i].externalId === externalId) {
      sameItemCount++;
    }
  }

  const scanned = sameItemCount < totalVerified;

  let verifiedBy: string | null = null;
  if (scanned) {
    let remaining = sameItemCount;
    for (const scan of userScans) {
      if (remaining < scan.quantityVerified) {
        verifiedBy = scan.verifiedBy;
        break;
      }
      remaining -= scan.quantityVerified;
    }
  }

  return { scanned, verifiedBy };
}

function getTotalScannedCount(row: FulfillmentGroupData): number {
  if (!row.scanVerifiedItemized) {
    return 0;
  }
  let total = 0;
  for (const userScans of Object.values(row.scanVerifiedItemized)) {
    for (const scan of userScans) {
      total += scan.quantityVerified;
    }
  }
  return total;
}

export function ScanVerifiedColumn({
  users,
  hideInputItems,
}: {
  users: OMSUser[];
  hideInputItems: boolean;
}): Column2<FulfillmentGroupData> {
  return {
    alignment: ColumnAlignment.LEFT,
    key: catalogEntry.key,
    title: catalogEntry.title,
    renderNormalCell: ({ row }) => {
      if (row.scanVerified) {
        const initials = getInitials(
          row.scanVerified.verifiedBy?.firstName,
          row.scanVerified.verifiedBy?.lastName,
          row.scanVerified.verifiedBy?.name,
        );
        return (
          <RedoTablePaddedCell>
            <RedoBadge
              color="success"
              size="xs"
              text={`Verified: ${initials}`}
            />
          </RedoTablePaddedCell>
        );
      }

      const scannedCount = getTotalScannedCount(row);
      if (scannedCount > 0) {
        const totalItems = getScanItemQuantity(row, { hideInputItems });
        return (
          <RedoTablePaddedCell>
            <RedoBadge
              color="gray"
              size="xs"
              text={`${scannedCount}/${totalItems} scanned`}
            />
          </RedoTablePaddedCell>
        );
      }

      return null;
    },
    renderExpandedCell: ({
      row,
      expansionIndex,
    }: {
      row: FulfillmentGroupData;
      expansionIndex: number;
    }) => {
      // Do not render an expanded cell if there is BOM expansion - because the items in the table are not actually the items that are scan verified. We can go back on this decision later if needed
      const hasBom = hasBomExpansion(row);
      if (hasBom) {
        return null;
      }
      if (row.scanVerified) {
        const initials = getInitials(
          row.scanVerified.verifiedBy?.firstName,
          row.scanVerified.verifiedBy?.lastName,
          row.scanVerified.verifiedBy?.name,
        );
        return (
          <RedoTablePaddedCell>
            <RedoBadge
              color="success"
              size="xs"
              text={`Verified: ${initials}`}
            />
          </RedoTablePaddedCell>
        );
      }

      const scanInfo = getItemScanInfo(row, expansionIndex);
      if (!scanInfo || !scanInfo.scanned) {
        return null;
      }

      const initials = scanInfo.verifiedBy
        ? getUserInitials(scanInfo.verifiedBy, users)
        : "";
      return (
        <RedoTablePaddedCell>
          <RedoBadge
            color="success"
            size="xs"
            text={initials ? `Scanned: ${initials}` : "Scanned"}
          />
        </RedoTablePaddedCell>
      );
    },
    width: catalogEntry.defaultWidth,
    sort: catalogEntry.sortDirection,
  };
}
